import React, {FC, useCallback, useEffect, useMemo, useState} from 'react';
import uuid from 'uuid-random';

import {useConstant, useDeepMemo} from '../../../../libs/shared/helpers/src/react/hooks/hookHelpers';
import {LegacyApplicationModel} from '../../../models/api/legacyApplicationModel';
import {LegacyEntryPoint} from '../../../models/api/legacyEntryPointModel';
import {TeammateModel} from '../../../models/api/teammateModel';
import {WidgetTemplateModel} from '../../../models/applications/widgetTemplateModel';
import {Dispatch} from '../../../store/appStore';
import {NewPanelId} from '../../../store/panels/panelStateBase';
import {useApplications} from '../legacyApplicationsContext';
import {LegacyContextBuilder} from './legacyApplicationContextBase';
import {LegacyApplicationContextWidget} from './legacyApplicationContextWidget';

/*
 * Props.
 */

interface LegacyApplicationContextHostProps {
  dispatch: Dispatch;
  panelId: NewPanelId;
  user: TeammateModel;
  application: LegacyApplicationModel;
  entryPoint: LegacyEntryPoint;
  preferences: object;
  contextBuilder: LegacyContextBuilder;
}

/*
 * Component.
 */

export const LegacyApplicationContextHost: FC<LegacyApplicationContextHostProps> = (props) => (
  <LegacyApplicationContextHostInternal key={props.entryPoint.uid} {...props} />
);

const LegacyApplicationContextHostInternal: FC<LegacyApplicationContextHostProps> = (props) => {
  const {dispatch, panelId, user, application, entryPoint, preferences, contextBuilder} = props;
  const {applicationId, entryPointId} = entryPoint;

  const {findSendContext} = useApplications();
  const sendContext = useMemo(() => findSendContext(applicationId), [findSendContext, applicationId]);

  /*
   * Application context.
   */

  const hostId = useConstant(() => uuid());
  const [widgets, setWidgets] = useState<ReadonlyArray<WidgetTemplateModel>>([]);

  const addWidget = useCallback((widget: WidgetTemplateModel) => {
    setWidgets((value) => [...value, widget]);
  }, []);

  const removeWidget = useCallback((widgetId: string) => {
    setWidgets((value) => value.filter((w) => w.id !== widgetId));
  }, []);

  const userPreferences = application.namespacePreferences[user.namespace];
  const authentication = useMemo(
    () => (userPreferences ? userPreferences.authentication : {}),
    [userPreferences],
  );

  const contextParameters = useDeepMemo(
    () => ({
      dispatch,
      panelId,
      user,
      applicationId,
      entryPointId,
      hostId,
      preferences,
      authentication,
      addWidget,
      removeWidget,
    }),
    [
      addWidget,
      panelId,
      applicationId,
      dispatch,
      entryPointId,
      hostId,
      preferences,
      authentication,
      removeWidget,
      user,
    ],
  );

  const context = useDeepMemo(() => contextBuilder(contextParameters), [contextParameters, contextBuilder]);

  // When the context changes, send it through the entry point.
  useEffect(() => {
    if (!sendContext) {
      return undefined;
    }
    return sendContext(context);
  }, [context, sendContext]);

  /*
   * Iframe render.
   */

  const widgetsRender = widgets.map((w) => (
    <LegacyApplicationContextWidget key={w.id} application={application} widget={w} />
  ));

  return <>{widgetsRender}</>;
};
